import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  TableSortLabel,
  TablePagination,
  useMediaQuery,
  useTheme,
  Button
} from '@mui/material';
import { Refresh, Info } from '@mui/icons-material';
import ApiService from '../../API/Requesthandler';
import { formatDistanceToNow, parseISO } from 'date-fns';

const STATUS_COLORS = {
  PROCESSING: 'warning',
  SENT: 'success',
  FAILED: 'error',
  COMPLETED: 'info'
};

const ENHANCED_COLORS = {
  SCHEDULE: 'primary',
  INSTANT: 'secondary',
  POSTPOND: 'info'
};

function MessagesLogger() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [state, setState] = useState({
    messages: [],
    loading: true,
    error: null,
    page: 0,
    rowsPerPage: 10,
    sortField: 'date_time',
    sortOrder: 'desc',
    filters: {
      type: 'all',
      status: 'all',
      date: 'all',
      search: ''
    }
  });

  const safeParseDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const getRelativeTime = (dateString) => {
    const date = safeParseDate(dateString);
    if (!date) return 'Invalid date';
    
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    return `${days} days ago`;
  };

  // Update your fetchMessages function
const fetchMessages = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const userData = JSON.parse(localStorage.getItem("user-data"));
      const response = await ApiService.get(`/api/chat/get_messages_logs/${userData.id}`);
  
      if (response.success) {
        const validatedMessages = response.data.map(msg => ({
          ...msg,
          date_time: safeParseDate(msg.date_time) || new Date().toISOString()
        }));
        
        setState(prev => ({
          ...prev,
          messages: validatedMessages.sort((a, b) => 
            new Date(b.date_time) - new Date(a.date_time)
          ),
          loading: false
        }));
      } else {
        throw new Error('Failed to load messages');
      }
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleSort = (field) => {
    const isAsc = state.sortField === field && state.sortOrder === 'asc';
    setState(prev => ({
      ...prev,
      sortField: field,
      sortOrder: isAsc ? 'desc' : 'asc'
    }));
  };

  const handleChangePage = (event, newPage) => {
    setState(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setState(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, [filterType]: value },
      page: 0
    }));
  };
  const isDateInRange = (dateString, range) => {
    const date = new Date(dateString);
    const now = new Date();
    
    switch(range) {
      case 'today': return date.toDateString() === now.toDateString();
      case 'week': return date >= new Date(now.setDate(now.getDate() - now.getDay()));
      case 'month': return date.getMonth() === now.getMonth();
      default: return true;
    }
  };

  const filteredMessages = state.messages.filter(message => {
    const { type, status, date, search } = state.filters;
    const searchLower = search.toLowerCase();
    
    return (
      (type === 'all' || message.message_type === type) &&
      (status === 'all' || message.status === status) &&
      (message.message_text.toLowerCase().includes(searchLower) ||
       message.message_header?.toLowerCase().includes(searchLower)) &&
      isDateInRange(message.date_time, date)
    );
  });

  

  const sortedMessages = filteredMessages.sort((a, b) => {
    const modifier = state.sortOrder === 'asc' ? 1 : -1;
    return a[state.sortField] > b[state.sortField] ? modifier : -modifier;
  });

  if (state.loading) {
    return (
      <Container maxWidth="xxl" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress size={60} thickness={4} />
      </Container>
    );
  }

  if (state.error) {
    return (
      <Container maxWidth="xxl" sx={{ mt: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={fetchMessages}>
              Retry
            </Button>
          }
        >
          {state.error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xxl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" sx={{ color: 'text.secondary' }}>
          Message History
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchMessages}
          sx={{ ml: 2 }}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Message Type</InputLabel>
            <Select
              value={state.filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="SCHEDULE">Scheduled</MenuItem>
              <MenuItem value="POSTPOND">Postponed</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={state.filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {Object.keys(STATUS_COLORS).map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Date Range</InputLabel>
            <Select
              value={state.filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={5}>
          <TextField
            fullWidth
            label="Search Messages"
            variant="outlined"
            size="small"
            value={state.filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              endAdornment: <Info fontSize="small" color="action" />
            }}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table stickyHeader aria-label="message log table">
          <TableHead>
            <TableRow>
              {[
                { id: 'id', label: 'ID', sortable: true },
                { id: 'message_header', label: 'Subject', sortable: true },
                { id: 'send_by', label: 'Sender', sortable: false },
                { id: 'recipients', label: 'Recipients', sortable: false },
                { id: 'message_text', label: 'Content', sortable: false },
                { id: 'number_of_files', label: 'Attachments', sortable: false },
                { id: 'message_type', label: 'Type', sortable: true },
                { id: 'status', label: 'Status', sortable: true },
                { id: 'date_time', label: 'Sent Time', sortable: true }
              ].map((column) => (
                <TableCell key={column.id}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={state.sortField === column.id}
                      direction={state.sortField === column.id ? state.sortOrder : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          
          <TableBody>
            {sortedMessages
              .slice(state.page * state.rowsPerPage, state.page * state.rowsPerPage + state.rowsPerPage)
              .map((message) => (
                <TableRow 
                  key={message.id}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>{message.id}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                      {message.message_header}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">{message.sender}</Typography>
                      {/* <Typography variant="caption" color="text.secondary">
                        {message.sender}
                      </Typography> */}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={message.recipients}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: isMobile ? 150 : 400 }}>
                    <Tooltip title={message.message_text}>
                      <Typography variant="body2" noWrap>
                        {message.message_text}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {message.number_of_files > 0 && (
                      <Chip
                        label={`${message.number_of_files} files`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={message.message_type}
                      color={ENHANCED_COLORS[message.message_type] || 'default'}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={message.status}
                      color={STATUS_COLORS[message.status] || 'default'}
                      variant="filled"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={safeParseDate(message.date_time)?.toLocaleString() || 'Invalid date'}>
                    <Typography variant="body2">
                        {getRelativeTime(message.date_time)}
                    </Typography>
                    </Tooltip>
                </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {filteredMessages.length === 0 && (
          <Box py={4} textAlign="center">
            <Typography variant="body1" color="text.secondary">
              No messages match the current filters
            </Typography>
          </Box>
        )}
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredMessages.length}
        rowsPerPage={state.rowsPerPage}
        page={state.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ mt: 2 }}
      />
    </Container>
  );
}

export default MessagesLogger;